// ClientHub data layer (spec 06_CLIENT). One wrapper per source, matching the
// repo's `{ error:false, data, message:'ok' }` / `throw { message }` convention.
//
// Two access shapes live here, both established elsewhere in this repo:
//   - RPCs are called via `supabase.rpc('fn_…', params)`.
//   - Views are read via `supabase.from('v_…').select('*')` — the read stays in
//     the wrapper, never in a component, so components never touch supabase.
//
// PROBE STATUS (2026-09-07): all 28 objects were confirmed live against
// jqmnhmoulybkggahkmzz (see 13_CLIENT_PROBE). Signatures and columns below match
// the probe. Two functions take a TEXT id, not uuid — fn_timeline(p_subject_id)
// and fn_drill(p_id) — so a raw uuid is cast to a string; passing the uuid type
// makes PostgREST answer PGRST202 (it looks like a missing function).
// v_requester_summary has no client_id (one row per person), so it is never
// filtered by client.
import { supabase } from '../../lib/supabase';
import { rpcReadWithRetry } from '../auth';

const wrap = (data) => ({ error: false, data, message: 'ok' });

// Guard against callers passing the literal string "undefined"/"null" (from
// stale localStorage or a lifecycle race) as a UUID — the same defensive shape
// auth.js uses, so a bad id becomes null rather than a 22P02 from Postgres.
const safeUuid = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === 'undefined' || s === 'null') return null;
  return s;
};

// ─── Shared view reader ──────────────────────────────────────────────────────
// Reads a whole view, or filters by a client column when `clientId` is given.
// The filter column defaults to `client_id`; a view that keys the client under
// a different column passes `clientColumn`.
async function readView(view, { clientId = null, clientColumn = 'client_id', label } = {}) {
  let q = supabase.from(view).select('*');
  const id = safeUuid(clientId);
  if (id) q = q.eq(clientColumn, id);
  const { data, error } = await q;
  if (error) throw { message: label ? `${label}: ${error.message}` : error.message };
  return wrap(data || []);
}

// ─── The list (spec §2) ──────────────────────────────────────────────────────
// One row per client. Read whole — the list page orders by consequence in the
// component, not in SQL.
export const getClientAttention = () =>
  readView('v_client_attention', { label: 'client attention' });

// One client's attention row — the detail hero composes from the same view the
// list orders by, so a client reads identically in both places. Returns the
// single row (or null), not the one-element array.
export const getClientAttentionRow = async (clientId) => {
  const resp = await readView('v_client_attention', { clientId, label: 'client attention' });
  return wrap((resp.data || [])[0] || null);
};

export const getClientEngagement = () =>
  readView('v_client_engagement', { label: 'client engagement' });

// With a client id it answers "is THIS client unowned?" — a row means yes, no row
// means it has an owner (name unknown: no read returns it yet).
export const getClientsWithoutOwner = (clientId = null) =>
  readView('v_clients_without_an_owner', { clientId, label: 'clients without an owner' });

export const getClientWaitingTime = () =>
  readView('v_client_waiting_time', { label: 'client waiting time' });

// The caller's FULL client roster — every client they can see (all clients for a
// platform admin), one row each. This is the authoritative client set (the same
// RPC the rest of the app uses); v_client_attention omits clients with no
// attention row, so it is not a substitute for "all clients". Shaped down to the
// { id, name } the list needs; enrichment (findings, churn) is joined in the page.
export const getMyClients = async () => {
  const { data, error } = await supabase.rpc('fn_get_my_clients');
  if (error) throw { message: `my clients: ${error.message}` };
  return wrap((data || []).map((r) => ({ id: r.id, name: r.name || '—' })));
};

// ─── The client page — primary (spec §3) ─────────────────────────────────────
// fn_client_context returns one jsonb: { client, attention, work, commitments,
// threads, people, billing, effort, elsewhere }. `billing` and `effort` come
// back NULL when the caller lacks billing.read / effort.read.all — that null IS
// the permission signal (RLS/claims enforced server-side). Pass the jsonb
// through UNTOUCHED so the null survives to the component, which renders
// <NotPermitted> for it (never zero, never empty).
//
// Wrapped in rpcReadWithRetry: this is the page's expensive root read and the
// one most likely to hit a cold-plan statement timeout (57014). The retry is
// scoped to 57014 only — auth/permission/bad-param errors surface immediately.
export const getClientContext = async (clientId) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const { data, error } = await rpcReadWithRetry('fn_client_context', { p_client_id: id });
  if (error) throw { message: error.message };
  return wrap(data);
};

// fn_client_evidence — the compact identity block for the standing-facts header.
// Absent fields are OMITTED (never null) by the function itself, so the header
// just renders what is present. Passed through untouched.
export const getClientEvidence = async (clientId) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const { data, error } = await supabase.rpc('fn_client_evidence', { p_client_id: id });
  if (error) throw { message: error.message };
  return wrap(data);
};

// ─── The client page — supplementary RPCs (spec §3) ──────────────────────────
export const getSignals = async (clientId) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const { data, error } = await supabase.rpc('fn_signals_for', { p_client_id: id });
  if (error) throw { message: error.message };
  return wrap(data);
};

// fn_timeline(p_subject_type text, p_subject_id TEXT, p_since, p_until,
// p_types text[], p_limit int, p_before_seq bigint) — the unified event stream.
// Reusable across subjects: client · task · thread · person. NOT `commitment` —
// the live function raises P0001 on it (verified 2026-09-09), so it is rejected
// here before the round-trip. p_subject_id is TEXT, so the id is cast to a
// string; passing the uuid type answers PGRST202 (reads as a missing function).
// `p_before_seq` is a sequence cursor (the previous page's next_before_seq),
// NOT a timestamp — two events in the same second cannot be lost or repeated.
// Returns { events[], available_types[], next_before_seq, … }. `available_types`
// drives the filter chips with no second call and no hardcoded list (KB §16).
const TIMELINE_SUBJECTS = ['client', 'task', 'thread', 'person'];
export const getTimeline = async (subjectType, subjectId, {
  since = null, until = null, types = null, limit = 100, beforeSeq = null,
} = {}) => {
  if (!TIMELINE_SUBJECTS.includes(subjectType)) {
    throw { message: `Invalid timeline subject type: ${subjectType}` };
  }
  const id = safeUuid(subjectId);
  if (!id) throw { message: 'Invalid subject id' };
  const { data, error } = await rpcReadWithRetry('fn_timeline', {
    p_subject_type: subjectType,
    p_subject_id: String(id),
    p_since: since,
    p_until: until,
    p_types: types,
    p_limit: limit,
    p_before_seq: beforeSeq,
  });
  if (error) throw { message: error.message };
  return wrap(data);
};

// fn_timeline_days(p_client_id, 90) — the per-day sparkline.
export const getTimelineDays = async (clientId, days = 90) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const { data, error } = await supabase.rpc('fn_timeline_days', { p_client_id: id, p_days: days });
  if (error) throw { message: error.message };
  return wrap(data);
};

export const suggestRecipient = async (clientId) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const { data, error } = await supabase.rpc('fn_suggest_recipient', { p_client_id: id });
  if (error) throw { message: error.message };
  return wrap(data);
};

export const suggestAccountOwner = async (clientId) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const { data, error } = await supabase.rpc('fn_suggest_account_owner', { p_client_id: id });
  if (error) throw { message: error.message };
  return wrap(data);
};

// Suggested account owners for MANY clients in one call — what the Clients list needs
// (one row per unassigned client on the page). UNCONFIRMED: `fn_suggest_account_owners`
// and its `p_client_ids uuid[]` argument are the name and shape proposed to the backend,
// and nothing calls this until canSuggestOwnersInList() is true. Rows carry `client_id`
// plus the same fields as fn_suggest_account_owner (`member_id`, `name`, `score`, …).
export const suggestAccountOwnersBatch = async (clientIds = []) => {
  const ids = [...new Set((clientIds || []).map(safeUuid).filter(Boolean))];
  if (!ids.length) return wrap([]);
  const { data, error } = await supabase.rpc('fn_suggest_account_owners', { p_client_ids: ids });
  if (error) throw { message: error.message };
  return wrap(data);
};

// Assign a client's account owner. UNCONFIRMED and not reachable from the UI today:
// no such RPC exists yet — `fn_client_owner_set` is the name proposed to the backend
// (a proposal, not a confirmed name), and the only screen that would call it is behind
// canAssignAccountOwner(), which is false until the RPC is confirmed. A write, so
// never retried, and it waits for the session like every write here.
export const assignClientOwner = async (clientId, memberId) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const member = safeUuid(memberId);
  if (!member) throw { message: 'Invalid member id' };
  await supabase.auth.getSession();
  const { data, error } = await supabase.rpc('fn_client_owner_set', { p_client_id: id, p_member_id: member });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return wrap(data);
};

// fn_sla_for(client_id, service) — what we promise for THIS service. Returns
// first_response_minutes, time_to_commitment_hours, update_every_days, plus
// typical_days/p90_days flagged estimate_is_not_a_promise. Passed through so the
// component can honour that flag (never render an estimate as a commitment).
export const getSla = async (clientId, service) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const { data, error } = await supabase.rpc('fn_sla_for', { p_client_id: id, p_service: service ?? null });
  if (error) throw { message: error.message };
  return wrap(data);
};

// fn_drill(p_what text, p_id text default null, p_scope jsonb default '{}',
// p_limit int default 100) — the drill behind any clickable number. p_id is
// TEXT (cast the uuid) and p_scope is jsonb (an object, not the bare string
// 'client'); a uuid-typed id or a string scope answers PGRST202.
export const drill = async (what, id = null, scope = {}, limit = 100) => {
  const resolvedId = safeUuid(id);
  const { data, error } = await supabase.rpc('fn_drill', {
    p_what: what,
    p_id: resolvedId == null ? null : String(resolvedId),
    p_scope: scope || {},
    p_limit: limit,
  });
  if (error) throw { message: error.message };
  return wrap(data);
};

// fn_focus(p_type, p_id) — general form: a person's name or a task id opens the
// same ClientSlideOut panel. Returns the resolved client reference the slide-out
// uses to call fn_client_context / fn_signals_for / fn_timeline. UNCONFIRMED —
// see probe status at the top of this file. Used by <ClientSlideOut> when
// focusType + focusId are passed instead of clientId (future wiring).
export const focus = async (type, id) => {
  const { data, error } = await supabase.rpc('fn_focus', { p_type: type, p_id: safeUuid(id) });
  if (error) throw { message: error.message };
  return wrap(data);
};

// ─── Billing (spec §3.1) ─────────────────────────────────────────────────────
export const getClientBilling = (clientId = null) =>
  readView('v_client_billing', { clientId, label: 'client billing' });

export const getClientServices = (clientId = null) =>
  readView('v_client_services', { clientId, label: 'client services' });

export const getBillingAttention = () =>
  readView('v_billing_attention', { label: 'billing attention' });

// The four billing-mismatch views the probe confirmed populated (I5): paying
// clients with no work, work with no payment, a delivery gap, and a billing/
// contact email mismatch. Read whole — the panel orders by consequence.
export const getPayingNoWork = () =>
  readView('v_paying_no_work', { label: 'paying, no work' });

export const getWorkNoPayment = () =>
  readView('v_work_no_payment', { label: 'work, no payment' });

export const getServiceDeliveryGap = () =>
  readView('v_service_delivery_gap', { label: 'service delivery gap' });

export const getBillingEmailMismatch = () =>
  readView('v_billing_email_mismatch', { label: 'billing email mismatch' });

// fn_match_stripe_customer — PROPOSES a stripe link on four signals with a
// reason and writes NOTHING (spec §3.1). A read despite the "fn_" name.
export const matchStripeCustomer = async (stripeCustomerId) => {
  const { data, error } = await supabase.rpc('fn_match_stripe_customer', {
    p_stripe_customer_id: stripeCustomerId,
  });
  if (error) throw { message: error.message };
  return wrap(data);
};

// ─── Notes (spec §3.2) — an observation, not a message ───────────────────────
// ops_notes: id, client_id, thread_id, task_id, contact_id, note_type, title,
// body, is_pinned, is_ai_generated, created_by. Pinned notes sit with the
// standing facts.
export const getClientNotes = (clientId) =>
  readView('ops_notes', { clientId, label: 'client notes' });

// Write path — creating a PM observation. A write, so it is NOT retried (a
// timeout on a write is ambiguous about whether it committed).
export const createClientNote = async ({
  clientId, title, body, noteType = null, threadId = null, taskId = null, contactId = null, isPinned = false,
}) => {
  const id = safeUuid(clientId);
  if (!id) throw { message: 'Invalid client id' };
  const { data, error } = await supabase
    .from('ops_notes')
    .insert({
      client_id: id,
      title: title ?? null,
      body: body ?? null,
      note_type: noteType,
      thread_id: threadId,
      task_id: taskId,
      contact_id: contactId,
      is_pinned: isPinned,
    })
    .select()
    .maybeSingle();
  if (error) throw { message: error.message };
  return wrap(data);
};

// ─── Requests (spec §4) — what the client asked us for ───────────────────────
export const getClientRequests = (clientId = null) =>
  readView('v_client_requests', { clientId, label: 'client requests' });

// ─── People (spec §5) — who at the client actually replies ───────────────────
// v_requester_summary is ONE ROW PER PERSON and has NO client_id column, so it
// is never filtered by client — filtering it answers PGRST202. Per-client
// "who replies" comes from mv_contact_engagement, which keys on (client,
// contact); days_quiet on it is the earliest churn signal.
export const getRequesterSummary = () =>
  readView('v_requester_summary', { label: 'requester summary' });

export const getContactEngagement = (clientId = null) =>
  readView('mv_contact_engagement', { clientId, label: 'contact engagement' });

// ─── SLA live state (spec §8) ────────────────────────────────────────────────
export const getSlaStatus = (clientId = null) =>
  readView('v_sla_status', { clientId, label: 'SLA status' });

export const getSlaBreaches = (clientId = null) =>
  readView('v_sla_breaches', { clientId, label: 'SLA breaches' });

// ─── Compose / write (spec §7) ───────────────────────────────────────────────
// fn_compose_send is the ONLY write path for a message, and in the UI it is
// reached only through Charles's <Composer>. A write, so never retried.
export const composeSend = async (payload) => {
  // SESSION GATE — the Supabase JS client restores the session from localStorage
  // asynchronously on creation. Without waiting, a cold-load RPC goes out with no
  // bearer token, so fn_compose_send resolves no auth.uid() (P0001 / silent no-op).
  // Mirrors the read path (api/conversations.js convRpc).
  await supabase.auth.getSession();
  const { data, error } = await supabase.rpc('fn_compose_send', payload || {});
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  // fn_compose_send returns null (HTTP 200) when its guard silently rejects the
  // call — e.g. p_thread_id is a CHANNEL id (not a thread id): the message never
  // lands and PostgREST reports 200/null. Treat that as a real failure so the
  // composer surfaces an error instead of silently clearing the input.
  if (data == null) throw { message: 'Message was not sent — fn_compose_send stored nothing. If this is a channel, try sending from a thread.' };
  return wrap(data);
};

// ─── Workspace lens & capabilities (Epic 2 · 00_CONTRACT.md §10) ──────────────
// A workspace is a lens (who is looking); a product is whose data it is. The lens
// replaces the legacy role check — the database decides who sees what, and the
// menu is a convenience, not the wall (every function checks auth.uid() itself).
//
// fn_my_workspaces  → the caller's workspaces (one entry = no switcher)
// fn_in_workspace   → boolean: is the caller in this workspace?
// fn_has_capability → boolean: does the caller hold this capability key?
//
// All three were probed against the live Supabase on 2026-09-06 and confirmed to
// EXIST (fn_in_workspace + fn_has_capability return ✅; fn_my_workspaces returns ✅
// but needs a real JWT to return its array — the anon key gets a P0001). See
// docs/CLIENTHUB-RPC-GAPS.md.

export const getMyWorkspaces = async () => {
  const { data, error } = await supabase.rpc('fn_my_workspaces');
  if (error) throw { message: error.message };
  return wrap(data || []);
};

export const isInWorkspace = async (workspace) => {
  const { data, error } = await supabase.rpc('fn_in_workspace', {
    p_workspace: workspace,
  });
  if (error) throw { message: error.message };
  return wrap(data === true || data === true);
};

export const hasCapability = async (key) => {
  const { data, error } = await supabase.rpc('fn_has_capability', {
    p_key: key,
  });
  if (error) throw { message: error.message };
  return wrap(data === true || data === true);
};
